import { ActionIcon, Button, Divider, Group, Menu, Text, Tooltip } from "@mantine/core";
import { IconArchive, IconCheck, IconChevronDown } from "@tabler/icons-react";
import { AgGridReact } from "ag-grid-react";
import { FC, useState } from "react";
import { useHistory } from "react-router";
import {
  useGetProjectByProjectIdTableTemplatesQuery,
  usePatchProjectByProjectIdTableTemplateMutation,
} from "../../../../../../openapi";
import { ConfirmAbortModal } from "../../../../../components/ConfirmAbortModal/ConfirmAbortModal";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { DEFAULT_VISIBLE_ISSUE_COLUMNS } from "../../issueConstants";
import { selectActiveTableTemplate } from "../../issueSelectors";
import { loadIssueTableTemplateThunk } from "../../issueThunk";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";
import IssueBoardTemplateActions from "./IssueBoardTemplateActions";

const RIGHT_ACTIONS_WIDTH = 72;

const IssueBoardTemplates: FC<{ gridRef: React.RefObject<AgGridReact> }> = ({ gridRef }) => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueboardId = useAppSelector(selectIssueBoardId);
  const [archiveCandidateId, setArchiveCandidateId] = useState<string | null>(null);
  const [hoveredTemplateId, setHoveredTemplateId] = useState<string | null>(null);
  const [menuOpened, setMenuOpened] = useState(false);
  const [updateTableTemplate] = usePatchProjectByProjectIdTableTemplateMutation();

  const { data: tableTemplates = [] } = useGetProjectByProjectIdTableTemplatesQuery(
    { issueboardId, projectId },
    { skip: issueboardId == null || projectId == null },
  );

  const history = useHistory();
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const archiveCandidate = tableTemplates.find((template) => template._id === archiveCandidateId) ?? null;

  const buttonLabel = activeTemplate ? `Valgt mal: ${activeTemplate.name}` : "Velg mal";

  const resetTable = () => {
    setMenuOpened(false);
    dispatch(loadIssueTableTemplateThunk(null));
    history.push(`${history.location.pathname}`);
    gridRef.current?.api?.resetColumnState();
    gridRef?.current?.api?.setColumnsVisible(DEFAULT_VISIBLE_ISSUE_COLUMNS, true);
  };

  const archiveTemplate = async () => {
    if (archiveCandidate == null || projectId == null) return;

    await updateTableTemplate({
      projectId,
      body: {
        tableTemplate: {
          ...archiveCandidate,
          archived: true,
        },
      },
    });

    if (archiveCandidate._id === activeTemplate?._id) {
      resetTable();
    }

    setArchiveCandidateId(null);
  };

  return (
    <>
      <ConfirmAbortModal
        isOpen={archiveCandidate != null}
        onCancel={() => setArchiveCandidateId(null)}
        onConfirm={archiveTemplate}
        customHeader="Arkiver tabellmal"
        customText={`Er du sikker på at du vil arkivere malen "${archiveCandidate?.name ?? ""}"?`}
        customButtonText="Bekreft"
        customButtonIcon={<IconArchive size={16} />}
      />
      <Group wrap="nowrap" gap={2}>
        <Menu
          shadow="md"
          withArrow={false}
          position="bottom-start"
          closeOnItemClick
          withinPortal
          opened={menuOpened}
          onChange={setMenuOpened}
        >
          <Menu.Target>
            <Button
              variant="secondary-filled"
              c={activeTemplate == null ? "warmgray.8" : "mc-primary"}
              size="sm"
              pl="xs"
              pr={"xs"}
              style={{ borderRadius: "6px 0 0 6px", maxWidth: "320px" }}
              rightSection={<IconChevronDown size={14} />}
            >
              <Text>{buttonLabel}</Text>
            </Button>
          </Menu.Target>
          <Menu.Dropdown
            style={{
              width: "max-content",
              maxWidth: "min(32rem, calc(100vw - 2rem))",
            }}
          >
            {tableTemplates.map((template) => {
              const isSelected = template?._id === activeTemplate?._id;
              const showDelete = hoveredTemplateId === template._id;

              return (
                <Menu.Item
                  key={template._id}
                  onClick={() => {
                    setMenuOpened(false);
                    dispatch(loadIssueTableTemplateThunk(template));
                  }}
                  onMouseEnter={() => setHoveredTemplateId(template._id)}
                  onMouseLeave={() => setHoveredTemplateId((current) => (current === template._id ? null : current))}
                  style={{ fontSize: "0.9em" }}
                  color={isSelected ? "mc-primary" : undefined}
                  fw={isSelected ? 400 : 300}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      minWidth: 0,
                      gap: "8px",
                    }}
                  >
                    <span
                      title={template.name}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {template.name}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "4px",
                        width: `${RIGHT_ACTIONS_WIDTH}px`,
                        flexShrink: 0,
                        marginLeft: "auto",
                      }}
                    >
                      <div
                        style={{
                          width: "18px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {isSelected ? <IconCheck size={18} /> : null}
                      </div>
                      <Tooltip label="Arkiver mal" withArrow openDelay={300}>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          aria-label={`Arkiver ${template.name}`}
                          style={{
                            visibility: showDelete ? "visible" : "hidden",
                            opacity: showDelete ? 1 : 0,
                            transition: "opacity 120ms ease",
                          }}
                          tabIndex={showDelete ? 0 : -1}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setMenuOpened(false);
                            setArchiveCandidateId(template._id);
                          }}
                        >
                          <IconArchive size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </div>
                  </div>
                </Menu.Item>
              );
            })}
            {tableTemplates.length > 0 && (
              <>
                <Divider my={4} />
                <Menu.Item leftSection={<i className="fa fa-undo fa-fw" />} onClick={() => resetTable()}>
                  Reset
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
        <IssueBoardTemplateActions />
      </Group>
    </>
  );
};

export default IssueBoardTemplates;
